import * as z from "zod";
import type { LegacyPythonToolName } from "../tools/legacy-python-tools";

export const CHAR_LIMIT = 32_000;

export namespace Content {
	export const schema = z.object({
		text: z.string(),
		/**
		 * The web pages this answer's tools returned. An outside link in `text` renders as a link
		 * only when its URL is one of these, so text the model was steered into writing cannot
		 * carry account data off in a URL. Absent on answers that used no web tool.
		 */
		sources: z.array(z.string()).optional(),
	});
}
export type Content = z.infer<typeof Content.schema>;

/** Long enough for any real page; a longer one could carry data in itself. */
const MAX_SOURCE_URL_CHARS = 500;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isHttpsUrl = (url: string): boolean => {
	try {
		return new URL(url).protocol === "https:";
	} catch {
		return false;
	}
};

const isSafeSourceUrl = (url: string): boolean =>
	url.length <= MAX_SOURCE_URL_CHARS && !url.includes("@") && isHttpsUrl(url);

/**
 * The tools whose results are pages off the open web. Only these may add outside links: a tool
 * that echoed a URL from the record or an upload must not, since that URL is no search result.
 */
const WEB_TOOL_NAMES: ReadonlySet<string> = new Set([
	"ask_web",
	"run_character_check",
] satisfies LegacyPythonToolName[]);

const sourceUrlsOf = (holder: unknown): string[] => {
	const sources = isRecord(holder) ? holder.sources : undefined;
	if (!Array.isArray(sources)) return [];
	return sources
		.map((source) => (isRecord(source) ? source.url : undefined))
		.filter((url): url is string => typeof url === "string" && isSafeSourceUrl(url));
};

/**
 * The `sources[].url` a web tool returned -- `ask_web`'s own, or each of `run_character_check`'s
 * `subjects` -- keeping only plain https URLs.
 */
export const webSourceUrls = (toolName: string, result: unknown): string[] => {
	if (!WEB_TOOL_NAMES.has(toolName)) return [];
	const subjects = isRecord(result) ? result.subjects : undefined;
	return [
		...sourceUrlsOf(result),
		...(Array.isArray(subjects) ? subjects.flatMap(sourceUrlsOf) : []),
	];
};
